import React, { useState, useEffect } from "react";

import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { Formik, Form } from "formik";
import { FormSelect, FormTextField } from "../../components/forms/FormikFields";
import yup from "../../commons/validators/customYup";
import MigrateEnrollmentService from "../../services/MigrateEnrollmentService";
import showNotification from "../../commons/helpers/showNotification";
import LabelStatus from "../../components/labels/LabelStatus";

const schemaSearch = yup.object().shape({
  email: yup.string().email().required(),
});

const schemaMigrate = yup.object().shape({
  newPlanId: yup.number().required(),
  oldPlanId: yup.number().required(),
  enrollmentId: yup.number().required(),
});

// COMPONENT
function MigratePlanPage() {
  const [selectedCompany, setSelectedCompany] = useState();
  const [newPlansDataSource, setNewPlansDataSource] = useState([]);
  const [oldPlansDataSource, setOldPlansDataSource] = useState([]);
  const [enrollmentsDataSource, setEnrollmentsDataSource] = useState([]);
  const [migratedEnrollments, setMigratedEnrollments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedCompany != null) {
      setIsLoading(true);

      MigrateEnrollmentService.getMonthlyPlans(selectedCompany.id)
        .then((resp) => {
          setNewPlansDataSource(
            resp.map((o) => ({
              id: o.id,
              name: `${o.name} - ${o.billingFrequency} - ${o.numberSessionsPerWeek}x semana`,
            })),
          );
          setOldPlansDataSource([]);
          setEnrollmentsDataSource([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setNewPlansDataSource([]);
      setOldPlansDataSource([]);
      setEnrollmentsDataSource([]);
    }
  }, [selectedCompany]);

  const handleNewChangePlan = (event) => {
    if (event.target != null && event.target.value != null) {
      setIsLoading(true);
      MigrateEnrollmentService.getNonMonthlyOldPlans(event.target.value)
        .then((resp) => {
          setOldPlansDataSource(
            resp.map((o) => ({
              id: o.id,
              name: `${o.name} - ${o.billingFrequency} - ${o.numberSessionsPerWeek}x semana`,
            })),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setOldPlansDataSource([]);
    }
  };

  const handleOldChangePlan = (event) => {
    if (event.target != null && event.target.value != null) {
      setIsLoading(true);
      MigrateEnrollmentService.getAllowedEnrollmentsForMigration(
        event.target.value,
      )
        .then((resp) => {
          setEnrollmentsDataSource(
            resp.map((o) => ({
              id: o.id,
              name: `${o.customerName} - ${o.planName} - ${o.billingFrequency}`,
            })),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setEnrollmentsDataSource([]);
    }
  };

  const myHandleSubmit = (values, actions) => {
    MigrateEnrollmentService.searchCompany(values.email)
      .then((companyFound) => {
        if (companyFound == null || companyFound === "") {
          showNotification("warning", "Nenhuma empresa encontrada.");
        } else {
          setSelectedCompany(companyFound);
        }
      })
      .finally(() => {
        actions.setSubmitting(false);
      });
  };

  const myMandleMigrate = (values, actions) => {
    MigrateEnrollmentService.migrate({
      planId: values.newPlanId,
      enrollmentId: values.enrollmentId,
    })
      .then((resp) => {
        const migratedEnrollment = enrollmentsDataSource.find(
          (p) => p.id === values.enrollmentId,
        );
        const newPlanSelected = newPlansDataSource.find(
          (p) => p.id === values.newPlanId,
        );
        const oldPlanSelected = oldPlansDataSource.find(
          (p) => p.id === values.oldPlanId,
        );

        if (
          migratedEnrollment != null &&
          newPlanSelected != null &&
          oldPlanSelected != null
        ) {
          // remove from data source
          setEnrollmentsDataSource((prev) =>
            prev.filter((p) => p.id !== values.enrollmentId),
          );

          // display as migrated
          setMigratedEnrollments((prev) => [
            ...prev,
            {
              ...migratedEnrollment,
              newPlanName: newPlanSelected.name,
              oldPlanName: oldPlanSelected.name,
            },
          ]);
        }
        showNotification("success", "Migrado com sucesso");
      })
      .finally(() => {
        actions.setSubmitting(false);
      });
  };

  return (
    <Box sx={{ s: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Formik
          enableReinitialize
          initialValues={{ email: "" }}
          validationSchema={schemaSearch}
          onSubmit={myHandleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <FormTextField
                  name="email"
                  label="Procure Empresa por email"
                  sx={{ minWidth: "300px" }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ ml: 2 }}
                >
                  Buscar {isSubmitting && <CircularProgress />}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>

      {selectedCompany && (
        <Box sx={{ my: 5 }}>
          <Box sx={{ my: 2 }}>
            <Typography>Empresa: {selectedCompany.name}</Typography>
            <LabelStatus status="info">
              Selecione abaixo a matricula e o plano
            </LabelStatus>
          </Box>

          {isLoading && <CircularProgress />}

          <Formik
            enableReinitialize
            initialValues={{ newPlanId: "", oldPlanId: "", enrollmentId: "" }}
            validationSchema={schemaMigrate}
            onSubmit={myMandleMigrate}
          >
            {({ isSubmitting }) => (
              <Form>
                <FormSelect
                  fullWidth
                  label="Novo Plano"
                  name="newPlanId"
                  placeholder="Selecione o plano ..."
                  dataSource={newPlansDataSource}
                  disabled={isLoading}
                  onChange={handleNewChangePlan}
                />
                <FormSelect
                  fullWidth
                  label="Plano Antigo"
                  name="oldPlanId"
                  placeholder="Selecione o plano ..."
                  dataSource={oldPlansDataSource}
                  disabled={isLoading}
                  onChange={handleOldChangePlan}
                />
                <FormSelect
                  fullWidth
                  label="Matricula"
                  name="enrollmentId"
                  placeholder="Selecione a matricula ..."
                  dataSource={enrollmentsDataSource}
                  disabled={isLoading}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ ml: 2 }}
                >
                  Migrar {isSubmitting && <CircularProgress />}
                </Button>
              </Form>
            )}
          </Formik>

          {/* TABLE */}
          {migratedEnrollments?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography>Matriculas migradas</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Enrollment Info</TableCell>
                    <TableCell>Novo Plano</TableCell>
                    <TableCell>Antigo Plano</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {migratedEnrollments?.map((e) => {
                    return (
                      <TableRow key={e.id}>
                        <TableCell>{e.name}</TableCell>
                        <TableCell>{e.newPlanName}</TableCell>
                        <TableCell>{e.oldPlanName}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default MigratePlanPage;
